import { t } from "ttag";
import * as Yup from "yup";

import {
  getPerformanceTabMetadata,
  getPositiveIntegerSchema,
} from "metabase/admin/performance/constants/complex";
import {
  PerformanceTabId,
  type StrategyData,
} from "metabase/admin/performance/types";
import type Question from "metabase-lib/v1/Question";
import { CacheDurationUnit } from "metabase-types/api";
import type { AdminPath } from "metabase-types/store";

const durationStrategyValidationSchema = Yup.object({
  type: Yup.string().equals(["duration"]),
  duration: getPositiveIntegerSchema().default(24),
  unit: Yup.string().oneOf(Object.values(CacheDurationUnit).map(String)),
  refresh_automatically: Yup.boolean().nullable().default(false),
});

export const ossGranularCachingStrategies: Record<string, StrategyData> = {
  duration: {
    label: () => t`Duration: keep the cache for a fixed amount of time`,
    shortLabel: () => t`Duration`,
    validationSchema: durationStrategyValidationSchema,
  },
};

export const getOssGranularPerformanceTabMetadata = () => {
  const metadata = getPerformanceTabMetadata();
  const cacheSettingsTab = metadata.find(
    ({ key }) => key === "performance-cache-settings",
  );
  const databasesTab = metadata.find(
    ({ key }) => key === "performance-databases",
  );
  const modelsTab = metadata.find(({ key }) => key === "performance-models");

  const tabs: (AdminPath & { tabId: string })[] = [];
  if (cacheSettingsTab) {
    tabs.push(cacheSettingsTab as AdminPath & { tabId: string });
  }
  if (databasesTab) {
    tabs.push(databasesTab as AdminPath & { tabId: string });
  }
  tabs.push({
    name: t`Dashboard caching`,
    path: "/admin/performance/dashboards",
    key: "performance-dashboards",
    tabId: PerformanceTabId.Dashboards,
  });
  tabs.push({
    name: t`Question caching`,
    path: "/admin/performance/questions",
    key: "performance-questions",
    tabId: PerformanceTabId.Questions,
  });
  if (modelsTab) {
    tabs.push(modelsTab as AdminPath & { tabId: string });
  }

  return tabs;
};

export const GranularControlsExplanation = () => (
  <>
    &nbsp;
    {t`You can set up one caching policy for all your databases, or set up caching policies specific to each database.`}
  </>
);

export const hasQuestionCacheSection = (question: Question) => {
  const type = question.type();

  return (
    type !== "model" &&
    (question.canWrite() || question.lastQueryStart() != null)
  );
};
